import { defineStore } from 'pinia';
import api from '@/api';

export const useWarehouseStore = defineStore('warehouse', {
  state: () => ({
    // List state with pagination
    warehouses: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,

    // Single warehouse
    warehouse: null,

    // Zones
    zones: [],

    // Locations
    locations: [],

    // Loading states
    loading: false,
    zonesLoading: false,
    locationsLoading: false,

    // Error state
    error: null
  }),

  getters: {
    getWarehouses: (state) => state.warehouses,
    getWarehouse: (state) => state.warehouse,
    getZones: (state) => state.zones,
    getLocations: (state) => state.locations,
    getPagination: (state) => ({
      totalCount: state.totalCount,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      totalPages: state.totalPages
    }),
    isLoading: (state) => state.loading,
    isZonesLoading: (state) => state.zonesLoading,
    isLocationsLoading: (state) => state.locationsLoading,
    getError: (state) => state.error
  },

  actions: {
    // ====================================================================
    // Warehouse actions
    // ====================================================================

    async fetchWarehouses(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.getWarehouses(params);
        this.warehouses = resp.data.warehouses || [];
        this.totalCount = resp.data.totalCount;
        this.pageNumber = resp.data.pageNumber;
        this.pageSize = resp.data.pageSize;
        this.totalPages = resp.data.totalPages;
      } catch (e) {
        this.error = e;
        this.warehouses = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async fetchWarehouse(id) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.getWarehouse(id);
        this.warehouse = resp.data;
      } catch (e) {
        this.error = e;
        this.warehouse = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async createWarehouse(payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.createWarehouse(payload);
        this.warehouse = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async updateWarehouse(id, payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.updateWarehouse(id, payload);
        this.warehouse = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async deleteWarehouse(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.warehouse.deleteWarehouse(id);
        this.warehouse = null;
        this.warehouses = this.warehouses.filter(w => w.warehouseId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    // ====================================================================
    // Zone actions
    // ====================================================================

    async fetchZones(warehouseId) {
      this.zonesLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.getZones(warehouseId);
        this.zones = resp.data || [];
      } catch (e) {
        this.error = e;
        this.zones = [];
        throw e;
      } finally {
        this.zonesLoading = false;
      }
    },

    async createZone(warehouseId, payload) {
      this.zonesLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.createZone(warehouseId, payload);
        this.zones.push(resp.data);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.zonesLoading = false;
      }
    },

    async updateZone(warehouseId, zoneId, payload) {
      this.zonesLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.updateZone(warehouseId, zoneId, payload);
        const index = this.zones.findIndex(z => z.zoneId === zoneId);
        if (index >= 0) {
          this.zones[index] = resp.data;
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.zonesLoading = false;
      }
    },

    async deleteZone(warehouseId, zoneId) {
      this.zonesLoading = true;
      this.error = null;
      try {
        await api.warehouse.deleteZone(warehouseId, zoneId);
        this.zones = this.zones.filter(z => z.zoneId !== zoneId);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.zonesLoading = false;
      }
    },

    // ====================================================================
    // Location actions
    // ====================================================================

    async fetchLocations(warehouseId, zoneId) {
      this.locationsLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.getLocations(warehouseId, zoneId);
        this.locations = resp.data || [];
      } catch (e) {
        this.error = e;
        this.locations = [];
        throw e;
      } finally {
        this.locationsLoading = false;
      }
    },

    async createLocation(warehouseId, zoneId, payload) {
      this.locationsLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.createLocation(warehouseId, zoneId, payload);
        this.locations.push(resp.data);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.locationsLoading = false;
      }
    },

    async updateLocation(warehouseId, zoneId, locationId, payload) {
      this.locationsLoading = true;
      this.error = null;
      try {
        const resp = await api.warehouse.updateLocation(warehouseId, zoneId, locationId, payload);
        const index = this.locations.findIndex(l => l.locationId === locationId);
        if (index >= 0) {
          this.locations[index] = resp.data;
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.locationsLoading = false;
      }
    },

    async deleteLocation(warehouseId, zoneId, locationId) {
      this.locationsLoading = true;
      this.error = null;
      try {
        await api.warehouse.deleteLocation(warehouseId, zoneId, locationId);
        this.locations = this.locations.filter(l => l.locationId !== locationId);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.locationsLoading = false;
      }
    },

    // Clear methods
    clearWarehouses() {
      this.warehouses = [];
      this.totalCount = 0;
      this.pageNumber = 1;
      this.totalPages = 0;
    },

    clearWarehouse() {
      this.warehouse = null;
    },

    clearZones() {
      this.zones = [];
    },

    clearLocations() {
      this.locations = [];
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearWarehouses();
      this.clearWarehouse();
      this.clearZones();
      this.clearLocations();
      this.clearError();
    }
  }
});
